/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockIcon from '@material-ui/icons/Lock';
import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import {
  Grid,
  makeStyles,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Box,
} from '@material-ui/core';
import { KeyboardArrowRight, Refresh as RefreshIcon } from '@material-ui/icons';
import i18n from 'i18n';
import Button from 'components/Button';
import Permission from 'components/ValidPermission';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import {
  EnvironmentFields,
  InstanceStateEnum,
  ServerFields,
} from 'modules/environments/interfaces';
import ApplicationState from 'store/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import {
  blockServerActionsRequest,
  unblockServerActionsRequest,
  detachServerRequest,
  getAllServersRequest,
  publishNewImageRequest,
} from 'modules/environments/actions';
import { modalOpen, modalClose } from 'modules/modal/actions';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { ModalFields } from 'modules/modal/interfaces';
import { Alert } from '@material-ui/lab';
import {
  DetachServerRequestProps,
  GetAllServersRequestProps,
} from 'modules/environments/requestInterfaces';
import ButtonGroup from 'components/ButtonGroup';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { Formik } from 'formik';
import { formatHourMinute } from 'utils/helpers';
import palette from 'theme/palette';
import styles, { overrideModalBlockActions } from '../styles';
import { isFinalizingState } from '../status';
import { useServerActions } from '../useServerActions';
import PreparationServerFields from './PreparationServerFields';
import { blockSchema, unblockSchema } from './schema';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'can_connect_instance',
  'detach_preparation_server',
  'publish_image',
  'start_preparation_server',
  'stop_preparation_server',
];

const requiredPublicationSchedule: Array<keyof PermissionsData> = [
  'list_image_publication_schedule',
  'create_image_publication_schedule',
  'cancel_image_publication_schedule',
];

const blockServerInitialValues = {
  block_actions_time_duration: 30,
  block_reason: '',
};

const unblockServerInitialValues = {
  unblock_reason: '',
};

interface PreparationServerProps {
  companySelected: CompanyData | null;
  environmentSelected: EnvironmentFields | null;
  isLoadingPreparationServer: boolean;
  permissions?: PermissionsData;
  preparationServer: ServerFields;
  role?: RolesEnum;
  setPublicationTab: React.Dispatch<React.SetStateAction<string>>;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
  setHighlightSection: React.Dispatch<React.SetStateAction<string>>;
  detachServerRequest: (props: DetachServerRequestProps) => void;
  getAllServersRequest: (props: GetAllServersRequestProps) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  blockServerActionsRequest: (props) => void;
  unblockServerActionsRequest: (props) => void;
}

const PreparationServer = ({
  companySelected,
  environmentSelected,
  isLoadingPreparationServer,
  permissions,
  preparationServer,
  role,
  setPublicationTab,
  setCurrentTab,
  setHighlightSection,
  detachServerRequest,
  getAllServersRequest,
  modalClose,
  modalOpen,
  blockServerActionsRequest,
  unblockServerActionsRequest,
}: PreparationServerProps) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const {
    handleConnect,
    handleStartServer,
    handleStopServer,
  } = useServerActions();
  const [refresh, setRefresh] = React.useState(true);
  const [stopRequest, setStopRequest] = React.useState(true);
  const [publicationLimitReached, setPublicationLimitReached] = React.useState(
    false
  );
  const [
    publicationLimitReachedDaily,
    setPublicationLimitReachedDaily,
  ] = React.useState(false);
  const intervalRef = React.useRef<any>();
  const RELOAD_TIME =
    environmentSelected?.provider?.type === 'oci' ? 15000 : 5000;

  const getPreparationServer = (refresh = false, getFromProvider = false) => {
    getAllServersRequest({
      id,
      force_refresh: getFromProvider,
      type: 'preparation',
    });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getPreparationServer();
  }, [id]);

  React.useEffect(() => {
    const stop =
      isFinalizingState(preparationServer?.image_publication_status) &&
      isFinalizingState(preparationServer?.instance_state);

    setStopRequest(stop || !preparationServer);
  }, [preparationServer]);

  React.useEffect(() => {
    if (stopRequest) {
      clearInterval(intervalRef.current);
      setRefresh(false);
    } else {
      intervalRef.current = setInterval(() => {
        getPreparationServer(true);
      }, RELOAD_TIME);
    }

    return () => clearInterval(intervalRef.current);
  }, [stopRequest]);

  React.useEffect(() => {
    if (
      environmentSelected &&
      environmentSelected?.behavior?.type !== 'manual'
    ) {
      setPublicationLimitReached(
        environmentSelected?.publication
          ?.executed_instance_blocking_publications >=
          environmentSelected?.publication?.limit_instance_blocking_publications
      );
      if (
        environmentSelected?.publication
          ?.limit_instance_blocking_publications_daily !== null
      ) {
        setPublicationLimitReachedDaily(
          environmentSelected?.publication
            ?.executed_instance_blocking_publications_daily >=
            Number(
              environmentSelected?.publication
                ?.limit_instance_blocking_publications_daily
            )
        );
      }
    } else {
      setPublicationLimitReached(false);
      setPublicationLimitReachedDaily(false);
    }
  }, [environmentSelected]);

  const isActionsBlockedForOperator =
    preparationServer?.block_actions_enabled &&
    !preparationServer?.can_execute_actions_on_blocked_server;

  const handleSubmitBlockServer = values => {
    blockServerActionsRequest({
      environmentUuid: id,
      instanceId: preparationServer.instance_id,
      values,
    });
    modalClose();
  };

  const handleSubmitUnblockServer = values => {
    unblockServerActionsRequest({
      environmentUuid: id,
      instanceId: preparationServer.instance_id,
      values,
    });
    modalClose();
  };

  const handleBlockActionsServerPreparation = () =>
    modalOpen({
      title: i18n.t('BLOCK_ACTIONS_SERVER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid container spacing={2}>
          <Grid item>
            <Typography className={classes.blockActionsDescription}>
              {i18n.t('BLOCKING_SERVER_ACTIONS_DESCRIPTION_KEY')}
            </Typography>
          </Grid>
          <Formik
            initialValues={blockServerInitialValues}
            onSubmit={handleSubmitBlockServer}
            enableReinitialize
            validationSchema={blockSchema}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.reasonField}
              >
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('BLOCKING_TIME_SERVER_KEY'),
                      stateKey: 'block_actions_time_duration',
                      isSelect: true,
                      options: [
                        { label: `30 ${i18n.t('MINUTES_KEY')}`, value: 30 },
                        {
                          label: `60 ${i18n.t('MINUTES_KEY')}`,
                          value: 60,
                        },
                        {
                          label: `90 ${i18n.t('MINUTES_KEY')}`,
                          value: 90,
                        },
                      ],
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('BLOCKING_REASON_KEY'),
                      stateKey: 'block_reason',
                      isMultiline: true,
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBlockServerActions}
                >
                  <Button id="submit-button" submitButton onClick={submitForm}>
                    {i18n.t('BLOCK_KEY')}
                  </Button>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={modalClose}
                  >
                    {i18n.t('CANCEL_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  const handleUnblockActionsServerPreparation = () =>
    modalOpen({
      title: i18n.t('UNBLOCK_ACTIONS_SERVER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid container spacing={2}>
          <Grid item>
            {!isClientOperator(role) ? (
              <>
                <Typography className={classes.alertText}>
                  {i18n.t('UNBLOCKING_SERVER_ACTIONS_RESPONSIBLE_KEY', {
                    name: `${preparationServer?.blocked_by?.first_name} ${preparationServer?.blocked_by?.last_name} (${preparationServer?.blocked_by?.email})`,
                    time: formatHourMinute(preparationServer?.blocked_at),
                    unblockDate: formatHourMinute(
                      preparationServer?.unblock_time
                    ),
                  })}
                </Typography>
                <Typography className={classes.alertText} component="div">
                  <Box color={palette.blue} fontWeight="bold" display="inline">
                    {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                  </Box>
                  {preparationServer?.block_reason}
                </Typography>
              </>
            ) : (
              <Typography className={classes.alertText}>
                {i18n.t('SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY', {
                  unblockDate: formatHourMinute(
                    preparationServer?.unblock_time
                  ),
                })}
              </Typography>
            )}
            <Typography className={classes.alertText}>
              {i18n.t('UNBLOCKING_SERVER_ACTIONS_DESCRIPTION_KEY')}
            </Typography>
          </Grid>
          <Formik
            initialValues={unblockServerInitialValues}
            onSubmit={handleSubmitUnblockServer}
            enableReinitialize
            validationSchema={unblockSchema}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.reasonField}
              >
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('UNBLOCKING_REASON_KEY'),
                      stateKey: 'unblock_reason',
                      isMultiline: true,
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBlockServerActions}
                >
                  <Button id="submit-button" submitButton onClick={submitForm}>
                    {i18n.t('UNBLOCK_KEY')}
                  </Button>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={modalClose}
                  >
                    {i18n.t('CANCEL_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  const handleOpenBlockDetails = () =>
    modalOpen({
      title: i18n.t('BLOCKED_ACTIONS_PARTNER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid item>
          {!isClientOperator(role) ? (
            <Grid item>
              <Typography className={classes.alertText}>
                {i18n.t('BLOCKED_ACTIONS_ALERT_DESCRIPTION_KEY', {
                  name: `${preparationServer?.blocked_by?.first_name} ${preparationServer?.blocked_by?.last_name} (${preparationServer?.blocked_by?.email})`,
                  time: formatHourMinute(preparationServer?.blocked_at),
                })}
              </Typography>
              <Typography className={classes.alertText} component="div">
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                </Box>
                {preparationServer?.block_reason}
              </Typography>
              <Grid container justify="space-between">
                <Grid item>
                  <Typography className={classes.alertText}>
                    {i18n.t('BLOCKED_ACTIONS_RELEASE_KEY', {
                      unblockDate: formatHourMinute(
                        preparationServer?.unblock_time
                      ),
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography className={classes.alertText}>
              {i18n.t('SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY', {
                unblockDate: formatHourMinute(preparationServer?.unblock_time),
              })}
            </Typography>
          )}
        </Grid>
      ),
      footer: (
        <Grid>
          {permissions?.can_unblock_actions_preparation_server && (
            <Button
              variant="outlined"
              outlineBlue
              onClick={handleUnblockActionsServerPreparation}
            >
              {i18n.t('UNBLOCK_KEY')}
            </Button>
          )}
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleDetachServerPreparation = () =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_MSG_KEY', {
        environment: preparationServer?.name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              detachServerRequest({
                environmentUuid: id,
                instanceId: preparationServer?.instance_id,
                type: 'preparation',
              });
              modalClose();
            }}
          >
            {i18n.t('UNLINK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  return (
    <Grid container className={classes.container}>
      {publicationLimitReached && !publicationLimitReachedDaily && (
        <>
          <Alert className={classes.alert} severity="warning" color="warning">
            <span>
              {i18n.t('ENVIRONMENTS_PAGE.LIMIT_MSG_IMAGE_PUBLICATION_KEY')}
            </span>
          </Alert>
          <Divider className={classes.dividerWarning} />
        </>
      )}
      {publicationLimitReachedDaily && !publicationLimitReached && (
        <>
          <Alert className={classes.alert} severity="warning" color="warning">
            <span>
              {i18n.t(
                'ENVIRONMENTS_PAGE.LIMIT_DAILY_MSG_IMAGE_PUBLICATION_KEY'
              )}
            </span>
          </Alert>
          <Divider className={classes.dividerWarning} />
        </>
      )}
      {publicationLimitReachedDaily && publicationLimitReached && (
        <>
          <Alert className={classes.alert} severity="warning" color="warning">
            <span>
              {i18n.t('ENVIRONMENTS_PAGE.BOTH_LIMIT_MSG_IMAGE_PUBLICATION_KEY')}
            </span>
          </Alert>
          <Divider className={classes.dividerWarning} />
        </>
      )}

      <Grid
        container
        justify="space-between"
        alignItems="center"
        className={classes.header}
      >
        <Grid item className={classes.title}>
          <Typography variant="h6" component="h2">
            {i18n.t('ENVIRONMENTS_PAGE.PREPARATION_SERVER_KEY')}
          </Typography>
          {preparationServer?.has_image_publication_schedule && (
            <Grid
              item
              className={classes.publishSchedule}
              onClick={() => {
                if (
                  !!permissions &&
                  requiredPublicationSchedule.every(
                    permission => permissions[permission]
                  )
                ) {
                  setCurrentTab(1);
                  setPublicationTab('schedule');
                  setHighlightSection('scheduleListing');
                }
              }}
            >
              <ScheduleIcon className={classes.publishScheduleIcon} />
              <span>{i18n.t('SERVER_HAS_SCHEDULE_PUBLICATION_KEY')}</span>
            </Grid>
          )}
        </Grid>

        <Grid item className={classes.serverActionsContainer}>
          {permissions?.can_block_actions_preparation_server &&
            !preparationServer?.block_actions_enabled && (
              <Tooltip
                arrow
                title={i18n.t('BLOCK_ACTIONS_TOOLTIP_SERVER_KEY') || ''}
              >
                <IconButton onClick={handleBlockActionsServerPreparation}>
                  <LockOpenIcon className={classes.lockIcon} />
                </IconButton>
              </Tooltip>
            )}
          {preparationServer?.block_actions_enabled && (
            <Tooltip
              arrow
              title={
                permissions?.can_unblock_actions_preparation_server
                  ? i18n.t('UNBLOCK_ACTIONS_TOOLTIP_SERVER_KEY')
                  : i18n.t('BLOCKED_ACTIONS_PARTNER_KEY')
              }
            >
              <LockIcon
                className={classes.iconLock}
                onClick={() => {
                  if (permissions?.can_unblock_actions_preparation_server) {
                    handleUnblockActionsServerPreparation();
                  } else {
                    handleOpenBlockDetails();
                  }
                }}
              />
            </Tooltip>
          )}
          {preparationServer && (
            <Permission value={requiredActionsPermissions}>
              <ButtonGroup
                label={i18n.t('ACTIONS_KEY')}
                options={[
                  {
                    id: 'start-server',
                    label: i18n.t('ENVIRONMENTS_PAGE.START_SERVER_KEY'),
                    disabled: isActionsBlockedForOperator,
                    onClick: () => handleStartServer(preparationServer),
                    hidden:
                      !permissions?.start_preparation_server ||
                      preparationServer?.instance_state !==
                        InstanceStateEnum.STOPPED,
                  },
                  {
                    id: 'stop-server',
                    label: i18n.t('ENVIRONMENTS_PAGE.STOP_SERVER_KEY'),
                    disabled: isActionsBlockedForOperator,
                    onClick: () => handleStopServer(preparationServer),
                    hidden:
                      !permissions?.stop_preparation_server ||
                      preparationServer?.instance_state !==
                        InstanceStateEnum.RUNNING,
                  },
                  {
                    id: 'unlink',
                    label: i18n.t('UNLINK_KEY'),
                    disabled: isActionsBlockedForOperator,
                    onClick: () => handleDetachServerPreparation(),
                    hidden: !permissions?.detach_preparation_server,
                  },
                  {
                    id: 'publish-new-version',
                    label: i18n.t(
                      'ENVIRONMENTS_PAGE.PUBLICATIONS_NEW_VERSION_KEY'
                    ),
                    disabled: isActionsBlockedForOperator,
                    hidden:
                      !permissions?.publish_image &&
                      !permissions?.publish_image_v2,
                    onClick: () => setCurrentTab(1),
                  },
                  {
                    id: 'connect-as',
                    label: i18n.t('CONNECT_LIKE_KEY'),
                    disabled: isActionsBlockedForOperator,
                    icon: <KeyboardArrowRight className={classes.icon} />,
                    hidden: !permissions?.can_connect_instance,
                    subOptions: preparationServer?.connection_usernames?.length
                      ? preparationServer?.connection_usernames?.map(
                          (username, index) => ({
                            id: `connect-as-${index}`,
                            label: username,
                            onClick: () =>
                              handleConnect(
                                preparationServer?.instance_id,
                                username
                              ),
                          })
                        )
                      : [
                          {
                            label: i18n.t('NO_USERNAME_AVAILABLE_KEY'),
                            onClick: () => null,
                          },
                        ],
                  },
                  {
                    id: 'block-actions',
                    label: i18n.t('BLOCKING_ACTIONS_KEY'),
                    icon: <LockIcon className={classes.iconLock} />,
                    hidden:
                      !permissions?.can_block_actions_preparation_server ||
                      preparationServer?.block_actions_enabled,
                    onClick: () => handleBlockActionsServerPreparation(),
                  },
                  {
                    id: 'unblock-actions',
                    label: i18n.t('UNBLOCKING_ACTIONS_KEY'),
                    icon: <LockOpenIcon className={classes.iconLock} />,
                    hidden:
                      !permissions?.can_unblock_actions_preparation_server ||
                      !preparationServer?.block_actions_enabled,
                    onClick: () => handleUnblockActionsServerPreparation(),
                  },
                ]}
              />
            </Permission>
          )}
          <Button
            id="btn-sync-with-provider-preparation"
            onClick={() => getPreparationServer(false, true)}
            variant="outlined"
            startIcon={<RefreshIcon />}
            className={clsx({
              [classes.superAdminSyncButton]: inSuperAdminView(
                role,
                companySelected
              ),
              [classes.adminSyncButton]: inAdminView(role, companySelected),
              [classes.operatorSyncButton]: inOperatorView(
                role,
                companySelected
              ),
              [classes.syncButton]: companySelected,
            })}
          >
            {i18n.t('ENVIRONMENTS_PAGE.SYNC_WITH_PROVIDER')}
          </Button>
        </Grid>
        {preparationServer?.block_actions_enabled && (
          <Grid container>
            <Alert
              className={classes.alertBlock}
              severity="warning"
              color="info"
              icon={<LockIcon />}
            >
              <Grid item>
                {!isClientOperator(role) ? (
                  <Grid item>
                    <Typography className={classes.alertText}>
                      {i18n.t('BLOCKED_ACTIONS_ALERT_DESCRIPTION_KEY', {
                        name: `${preparationServer?.blocked_by?.first_name} ${preparationServer?.blocked_by?.last_name} (${preparationServer?.blocked_by?.email})`,
                        time: formatHourMinute(preparationServer?.blocked_at),
                      })}
                    </Typography>
                    <Typography className={classes.alertText} component="div">
                      <Box
                        color={palette.blue}
                        fontWeight="bold"
                        display="inline"
                      >
                        {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                      </Box>
                      {preparationServer?.block_reason}
                    </Typography>
                    <Grid container justify="space-between">
                      <Grid item>
                        <Typography className={classes.alertText}>
                          {i18n.t('BLOCKED_ACTIONS_RELEASE_KEY', {
                            unblockDate: formatHourMinute(
                              preparationServer?.unblock_time
                            ),
                          })}
                        </Typography>
                      </Grid>
                      {permissions?.can_unblock_actions_preparation_server ? (
                        <Button
                          variant="outlined"
                          outlineBlue
                          onClick={handleUnblockActionsServerPreparation}
                        >
                          {i18n.t('UNBLOCK_KEY')}
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container justify="space-between" alignItems="center">
                    <Grid item>
                      <Typography className={classes.alertText}>
                        {i18n.t('SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY', {
                          unblockDate: formatHourMinute(
                            preparationServer?.unblock_time
                          ),
                        })}
                      </Typography>
                    </Grid>
                    <Grid item>
                      {permissions?.can_unblock_actions_preparation_server ? (
                        <Button
                          variant="outlined"
                          outlineBlue
                          onClick={handleUnblockActionsServerPreparation}
                        >
                          {i18n.t('UNBLOCK_KEY')}
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Alert>
          </Grid>
        )}
      </Grid>

      <Grid
        container
        alignItems="center"
        spacing={2}
        className={classes.gridCircularProgress}
      >
        {isLoadingPreparationServer && !refresh ? (
          <CircularProgress className={classes.circularProgress} />
        ) : (
          <PreparationServerFields
            setCurrentTab={setCurrentTab}
            setHighlightSection={setHighlightSection}
            setPublicationTab={setPublicationTab}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  environments: {
    environmentSelected,
    preparationServer,
    isLoadingPreparationServer,
  },
}: ApplicationState) => ({
  companySelected,
  environmentSelected,
  isLoadingPreparationServer,
  permissions: data?.permissions,
  preparationServer,
  role: data?.role,
});

export default connect(mapStateToProps, {
  detachServerRequest,
  getAllServersRequest,
  modalClose,
  modalOpen,
  publishNewImageRequest,
  blockServerActionsRequest,
  unblockServerActionsRequest,
})(PreparationServer);
